import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
} from '@angular/core'
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { SignatureRole, SignerRoles } from '@tokeet-frontend/signature'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'

@Component({
  selector: 'app-form-role-input',
  templateUrl: './form-role-input.component.html',
  styleUrls: ['./form-role-input.component.scss'],
})
export class FormRoleInputComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() isEditable = true
  @Input() isDuplicated = false
  @Output() roleSelect = new EventEmitter<string>()

  @Input()
  get disabled() {
    return this.roleViewCtrl?.disabled
  }
  set disabled(disabled: boolean) {
    if (disabled) {
      this.roleViewCtrl.disable({ emitEvent: false })
    } else {
      this.roleViewCtrl.enable({ emitEvent: false })
    }
  }

  availableRoles = SignatureRole.getAvailableSignerRolesLabel()
  contactRoleLabel = SignerRoles.Guest
  userRoleLabel = SignerRoles.User

  // user input string, consider as User/Contact
  userRoles: string[] = [SignerRoles.User]
  contactRoles: string[] = [SignerRoles.Contact, SignerRoles.Guest]

  roleViewCtrl = new FormControl('', () => {
    return this.isDuplicated ? { duplicate: true } : null
  })

  private destroy$ = new Subject()
  notifyValueChange: (value: any) => void = () => {}
  notifyValueTouched = () => {}

  constructor(@Optional() @Self() public ngControl: NgControl) {
    if (ngControl) {
      ngControl.valueAccessor = this
    }
  }

  ngOnInit(): void {
    this.roleViewCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((viewValue) => {
      this.notifyValueTouched()
      this.notifyValueChange(this.getRoleValue(viewValue))
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDuplicated']) {
      this.roleViewCtrl.updateValueAndValidity()
    }
  }

  getRoleValue(t: string) {
    if (this.isUserRole(t)) {
      return this.userRoleLabel
    } else if (this.isContactRole(t)) {
      return this.contactRoleLabel
    }
    return t
  }

  isUserRole(r: string) {
    return this.userRoles.includes(r)
  }

  isContactRole(r: string) {
    return this.contactRoles.includes(r)
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  writeValue(value: any): void {
    this.roleViewCtrl.setValue(this.getRoleValue(value), { emitEvent: false })
  }

  registerOnChange(fn: (value: any) => void): void {
    this.notifyValueChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.notifyValueTouched = fn
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled
  }

  markTouched() {
    this.roleViewCtrl.markAsTouched()
  }

  onRoleSelect({ option }: MatAutocompleteSelectedEvent) {
    this.roleSelect.emit(option.value)
  }
}
